import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReviewerService } from '../../../core/services/reviewer.service';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-action-plan-detail-popup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './action-plan-detail-popup.component.html',
  styleUrl: './action-plan-detail-popup.component.css',
})
export class ActionPlanDetailPopupComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private reviewerService = inject(ReviewerService);
  private dateFormatSvc = inject(DateFormatService);

  /** ReviewID needed for the GetActionPlans call. */
  @Input() reviewId: number | null = null;
  /** Numeric Action Plan ID (without the AP- prefix). */
  @Input() actionPlanId: string | number | null = null;
  @Input() isGoLive = false;

  @Output() close = new EventEmitter<void>();

  isLoading = false;
  dataLoaded = false;

  actionPlan = {
    description: '',
    closedDate: '',
    internalDependency: '',
    targetDate: '',
    owners: [] as string[],
    actionPlanStatus: '',
    revisedTargetDate: '',
    rcComments: '',
    uploadedDocuments: [] as { fileName: string; documentId: number; storagePath: string }[],
    clientDependency: 'yes' as 'yes' | 'no',
    redClientObservationDate: '',
    targetDateToInformClient: '',
    legacyApNumber: '',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['actionPlanId'] && !changes['actionPlanId'].firstChange) {
      this.dataLoaded = false;
      if (this.reviewId != null && this.actionPlanId != null && this.actionPlanId !== '') {
        this.loadActionPlan();
      }
    }
  }

  ngOnInit(): void {
    if (this.reviewId == null || this.actionPlanId == null || this.actionPlanId === '') return;
    this.loadActionPlan();
  }

  private loadActionPlan(): void {
    this.isLoading = true;
    this.reviewerService.getActionPlansEAV(this.reviewId!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: any[]) => {
          const items = Array.isArray(list) ? list : [];
          const ap = items.find((a: any) => String(a.ActionPlanID) === String(this.actionPlanId));
          if (ap) {
            this.mapActionPlan(ap);
            this.dataLoaded = true;
          }
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('GetActionPlans error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  private mapActionPlan(ap: any): void {
    const rawOwners: string = ap.ActionRequiredBy ?? ap.OwnerEmail ?? '';
    const owners = rawOwners ? rawOwners.split(',').map((o: string) => o.trim()).filter(Boolean) : [];

    // Documents can arrive as an array or (rarely) a JSON string.
    let docsRaw: any = ap.Documents;
    if (typeof docsRaw === 'string' && docsRaw.trim()) {
      try { docsRaw = JSON.parse(docsRaw); } catch { docsRaw = []; }
    }
    const uploadedDocuments = Array.isArray(docsRaw)
      ? docsRaw.map((d: any) => ({
          fileName: d.FileName ?? d.fileName ?? '',
          documentId: d.ActionPlanDocumentID ?? d.DocumentID ?? d.documentId ?? 0,
          storagePath: d.StorageFilePath ?? d.storagePath ?? '',
        }))
      : [];

    const clientDep = (ap.ClientDependency ?? '').toLowerCase();

    this.actionPlan = {
      description: ap.ActionPlanDescription ?? ap.Description ?? '',
      closedDate: ap.ClosedDateForActionPlan ?? ap.ClosedDate ?? '',
      internalDependency: ap.InternalDependency ?? '',
      targetDate: ap.TargetDateForActionPlanRemediationAction ?? ap.TargetDate ?? '',
      owners,
      actionPlanStatus: ap.ActionPlanStatus ?? ap.AP_Status ?? '',
      revisedTargetDate: this.parseToInputDate(ap.RevisedTargetDate ?? ''),
      rcComments: ap.AP_RCComments ?? ap.RCComments ?? '',
      uploadedDocuments,
      clientDependency: clientDep === 'yes' ? 'yes' : 'no',
      redClientObservationDate: this.parseToInputDate(ap.AP_RedClientDate ?? ap.RedClientDate ?? ap.RedClientObservationDate ?? ''),
      targetDateToInformClient: this.parseToInputDate(ap.TargetDateclientinfo ?? ap.TargetDateToInformClient ?? ''),
      legacyApNumber: ap.LegacyAPNumber ?? ap.AP_LegacyApNumber ?? ap.LegacyApNumber ?? '',
    };
  }

  downloadDocument(doc: { storagePath: string; fileName: string }): void {
    this.reviewerService.downloadIssueDocument(doc.storagePath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.fileName || doc.storagePath.split('/').pop() || 'download';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err: any) => console.error('Download error:', err?.status, err?.message),
      });
  }

  formatDateDisplay(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  private parseToInputDate(value: string): string {
    if (!value) return '';
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const MONTHS: Record<string, string> = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
    };
    const dmy = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(value);
    if (dmy) return `${dmy[3]}-${MONTHS[dmy[2]] ?? '01'}-${dmy[1]}`;
    return '';
  }

  onCancel(): void {
    this.close.emit();
  }
}
